import { useEffect, useRef, useState } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import styles from "./UnitPriceForm.module.css";
import { FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";
import UnitPriceConfirmation from "./UnitPriceConfirmation";
import FieldInfo from "../MarketPlaceDetail/FieldInfo";
import { FIELD_INFO } from "../MarketPlaceDetail/fieldInfoContent";
import ActiveSubscriptionsBadge from "./ActiveSubscriptionsBadge";

const UnitPriceT2Form = ({
  allow_delete,
  unitPrices,
  onSave,
  inline = false,
  priceId,
}) => {
  const [showCard, setShowCard] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [unitPriceErrors, setUnitPriceErrors] = useState({});
  const [unitPriceForms, setUnitPriceForms] = useState([]);
  // FIX 4: snapshot the ORIGINAL DB per-band prices once, so the "changed"
  // detection diffs against the DB value (not the last live-committed one) and
  // an edit-then-revert no longer over-fires the active-subscription warning.
  const originalUnitPricesRef = useRef(null);
  if (originalUnitPricesRef.current === null) {
    originalUnitPricesRef.current = (unitPrices || []).map((u) => ({ ...u }));
  }

  useEffect(() => {
    if (unitPrices && unitPrices.length) {
      const withIds = unitPrices.map((u, i) => ({
        ...u,
        type: "existing",
        changed_unitprice: u.changed_unitprice ?? false,
      }));
      setUnitPriceForms(withIds);
    }
  }, [showCard, unitPrices]);

  const validateUnitPriceForms = (forms) => {
    const errors = {};
    let isValid = true;

    forms.forEach((form, index) => {
      const rowErrors = {};
      const requiredFields = [
        "Marketplace_price",
        "Industry_price",
        "Vendor_price",
        "hsn_code",
      ];
      requiredFields.forEach((field) => {
        const value = form[field];
        if (value === "" || value === null || value === undefined) {
          rowErrors[field] = true;
          isValid = false;
        }
      });
      if (Object.keys(rowErrors).length > 0) {
        errors[index] = rowErrors;
      }
    });
    if (Object.keys(errors).length) {
      toast.error("Add all required feilds");
      setUnitPriceErrors(errors);
    }
    return isValid;
  };

  const markChangedUnits = (forms = unitPriceForms) => {
    // FIX 4: diff against the ORIGINAL DB snapshot, not the live `unitPrices`.
    const originals = originalUnitPricesRef.current || [];
    return forms.map((curr, i) => {
      const original = originals[i];
      // FIX 2: prices are RAW STRINGS while typing — compare numerically so a
      // string "615.6" and DB number 615.6 don't read as a change.
      const numChanged = (f) => Number(curr[f]) !== Number(original[f]);

      // compare between old and new unit_price
      const changed =
        !original ||
        numChanged("Marketplace_price") ||
        numChanged("Vendor_price") ||
        numChanged("Industry_price") ||
        // these editable fields were silently skipped, so edits to them never
        // flagged the row as changed
        numChanged("Min_price") ||
        numChanged("Max_price") ||
        curr.hsn_code !== original.hsn_code;

      // return true if any changes in unit_price
      return {
        ...curr,
        changed_unitprice: changed,
      };
    });
  };

  // LIVE-COMMIT (inline mode): sync per-band prices into the plan's unit_ids on
  // every edit so the outer "Save" always has the latest — no separate "Apply
  // prices" click. Not gated on validation (transient invalid states still sync,
  // errors surface inline, outer Save runs its own gate). Row identity + the
  // capitalized backend keys are preserved by spreading each form untouched.
  const commitInline = (forms) => {
    if (!inline) return;
    onSave(markChangedUnits(forms));
  };

  const handleSave = () => {
    const isValid = validateUnitPriceForms(unitPriceForms);
    if (!isValid) {
      return;
    }
    //Calculate changes in required field
    const updatedUnits = markChangedUnits();

    // Inline: edits already committed live on change. Button is a
    // validate/confirm affordance; the active-subscription confirm is on the
    // outer Save.
    if (inline) {
      onSave(updatedUnits);
      toast.success("Prices applied");
      return;
    }

    const changedSomething = updatedUnits.some((u) => u.changed_unitprice);

    if (!allow_delete && changedSomething) {
      setShowConfirmation(true);
      return;
    } else {
      setShowCard(false);
      onSave(updatedUnits);
      return;
    }
  };

  const handleSaveFromConfirmation = () => {
    const updatedUnits = markChangedUnits();
    setShowConfirmation(false);
    setShowCard(false);
    onSave(updatedUnits);
    return;
  };

  const handleChange = (index, e) => {
    const { name, value, type, checked } = e.target;
    const realValue =
      type === "checkbox"
        ? checked
        : type === "number"
          ? value === ""
            ? ""
            : Number(value)
          : value;
    let updatedForms;
    updatedForms = unitPriceForms.map((form, i) =>
      i === index ? { ...form, [name]: realValue } : form,
    );
    setUnitPriceForms(updatedForms);
    setUnitPriceErrors((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [name]: false,
      },
    }));
    commitInline(updatedForms);
  };

  const bodyInner =
    unitPriceForms.length !== 0 ? (
      <>
        {unitPriceForms.map((form, index) => (
          <Form
            key={index}
            className={styles.card}
            onSubmit={(e) => e.preventDefault()}
          >
            <div className={styles.unitPriceTop}>
              <h1 className={styles.heading}>
                Range {form.StartRange} - {form.EndRange}
              </h1>
            </div>
            <div className={styles.cardBody}>
              <Row>
                <Col md={4}>
                  <Form.Group controlId={`formMarketplacePrice-${index}`}>
                    <Form.Label className={styles.unitPriceLabel}>
                      Cost/unit in this range eg: 600(cost of end range(in the
                      listed currency)) / 300 (End Range) = 2(in the listed
                      currency) cost/unit
                      <FieldInfo text={FIELD_INFO["unit.marketplace_price"]} />
                    </Form.Label>
                    <Form.Control
                      required
                      type="text"
                      inputMode="decimal"
                      name="Marketplace_price"
                      value={form.Marketplace_price}
                      onChange={(e) => handleChange(index, e)}
                      placeholder="Cost / unit of this Range"
                      className={`${styles.formControl} ${
                        unitPriceErrors[index]?.Marketplace_price
                          ? styles.inputError
                          : ""
                      }`}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group controlId={`formIndustryPrice-${index}`}>
                    <Form.Label className={styles.unitPriceLabel}>
                      Industry Price
                      <FieldInfo text={FIELD_INFO["unit.industry_price"]} />
                    </Form.Label>
                    <Form.Control
                      required
                      type="text"
                      inputMode="decimal"
                      name="Industry_price"
                      value={form.Industry_price}
                      onChange={(e) => handleChange(index, e)}
                      placeholder="Industry Price"
                      className={`${styles.formControl} ${
                        unitPriceErrors[index]?.Industry_price
                          ? styles.inputError
                          : ""
                      }`}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId={`formVendorPrice-${index}`}>
                    <Form.Label className={styles.unitPriceLabel}>
                      Vendor Price
                      <FieldInfo text={FIELD_INFO["unit.vendor_price"]} />
                    </Form.Label>
                    <Form.Control
                      required
                      type="text"
                      inputMode="decimal"
                      name="Vendor_price"
                      value={form.Vendor_price}
                      onChange={(e) => handleChange(index, e)}
                      placeholder="Vendor Price"
                      className={`${styles.formControl} ${
                        unitPriceErrors[index]?.Vendor_price
                          ? styles.inputError
                          : ""
                      }`}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId={`formMinPrice-${index}`}>
                    <Form.Label className={styles.unitPriceLabel}>
                      Min Price
                      <FieldInfo text={FIELD_INFO["unit.min_price"]} />
                    </Form.Label>
                    <Form.Control
                      required
                      type="text"
                      inputMode="decimal"
                      name="Min_price"
                      value={form.Min_price}
                      onChange={(e) => handleChange(index, e)}
                      placeholder="Min Price"
                      className={styles.formControl}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group controlId={`formMaxPrice-${index}`}>
                    <Form.Label className={styles.unitPriceLabel}>
                      Max Price
                      <FieldInfo text={FIELD_INFO["unit.max_price"]} />
                    </Form.Label>
                    <Form.Control
                      required
                      type="text"
                      inputMode="decimal"
                      name="Max_price"
                      value={form.Max_price}
                      onChange={(e) => handleChange(index, e)}
                      placeholder="Max Price"
                      className={styles.formControl}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId={`formHsn-${index}`}>
                    <Form.Label className={styles.unitPriceLabel}>
                      HSN Code
                      <FieldInfo text={FIELD_INFO["unit.hsn_code"]} />
                    </Form.Label>
                    <Form.Control
                      required
                      type="string"
                      name="hsn_code"
                      value={form.hsn_code}
                      onChange={(e) => handleChange(index, e)}
                      placeholder="HSN Code"
                      className={`${styles.formControl} ${
                        unitPriceErrors[index]?.hsn_code
                          ? styles.inputError
                          : ""
                      }`}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Form>
        ))}
        <Button className={styles.submitBtn} onClick={handleSave}>
          {inline ? "Apply prices" : "Submit"}
        </Button>
      </>
    ) : (
      <p>Define credit tiers (ranges &amp; steps) above first.</p>
    );

  return (
    <div className={styles.container}>
      {!inline && (
        <button
          className={styles.EditUnitPrice}
          onClick={(e) => {
            e.preventDefault();
            setShowCard(true);
          }}
          aria-haspopup="dialog"
          aria-expanded={showCard}
        >
          <FaEdit />
        </button>
      )}
      {inline ? (
        <div className={styles.inlineUnitBody}>
          {!allow_delete && (
            <div className={styles.activeSubWarning}>
              <span aria-hidden="true">⚠</span>
              <span style={{ flex: 1 }}>
                This plan has active subscriptions — changing these prices will
                re-price existing customers at their next renewal.
              </span>
              <ActiveSubscriptionsBadge priceId={priceId} />
            </div>
          )}
          {bodyInner}
        </div>
      ) : (
        showCard && (
          <div className={styles.unitPriceContainer}>
            <div
              className={styles.dropShadow}
              onClick={() => setShowCard(false)}
              aria-hidden="true"
            />
            <div
              className={styles.unitPricefield}
              role="dialog"
              aria-modal="true"
              aria-label="Unit Prices"
              onKeyDown={(e) => {
                if (e.key === "Escape") setShowCard(false);
              }}
            >
              <div className={styles.unitBody}>{bodyInner}</div>
            </div>
          </div>
        )
      )}
      {showConfirmation && (
        <UnitPriceConfirmation
          onClose={() => setShowConfirmation(false)}
          onSave={() => {
            handleSaveFromConfirmation();
          }}
        />
      )}
    </div>
  );
};

export default UnitPriceT2Form;
